import { useMemo } from 'react'
import { type EnterprisePermission, type EnterpriseRole } from '../../../../__codegen__/api'
import { type NavMenuHierarchyL2 } from '../../nav-v2/NavMenuHierarchy'
import { getPermissions } from '../utils'

const getAssociatedRolesFromToolPermissions = (
  associatedRoles: Set<{ label: string; permissions: { slug: string }[] }>,
  toolPermissionSlugs: Set<string>,
  rolesToPermissions: Map<{ label: string; permissions: { slug: string }[] }, Set<string>>
) => {
  rolesToPermissions.forEach((rolePermissions, role) => {
    if (rolePermissions.isSupersetOf(toolPermissionSlugs)) {
      associatedRoles.add(role)
    }
  })
  return associatedRoles
}

const getAssociatedRolesFromInToolPermissions = (
  associatedRoles: Set<{ label: string; permissions: { slug: string }[] }>,
  inToolPermissions: Set<string>,
  rolesToPermissions: Map<{ label: string; permissions: { slug: string }[] }, Set<string>>
) => {
  rolesToPermissions.forEach((rolePermissions, role) => {
    if (inToolPermissions.intersection(rolePermissions).size > 0) {
      associatedRoles.add(role)
    }
  })
  return associatedRoles
}

export const usePermissionCalculations = (
  tool: NavMenuHierarchyL2,
  userRoles?: { label: string; permissions: { slug: string }[] }[],
  roleAssignedPermissionSlugs?: Set<string>
) => {
  // Create a map of roles to a set of their permission slugs
  const rolesToPermissions = useMemo(
    () =>
      userRoles?.reduce((acc, role) => {
        acc.set(role, new Set(role.permissions?.map(permission => permission.slug) || []))
        return acc
      }, new Map<{ label: string; permissions: { slug: string }[] }, Set<string>>()),
    [userRoles]
  )

  const toolRequiredPermissionSet = useMemo(
    () => new Set(getPermissions(tool.accessControl)[0]),
    [tool.accessControl]
  )

  // Calculate which roles are associated with the current tool
  const associatedRoles = useMemo(() => {
    const associatedRoles = new Set<{ label: string; permissions: { slug: string }[] }>()
    if (rolesToPermissions) {
      getAssociatedRolesFromToolPermissions(
        associatedRoles,
        toolRequiredPermissionSet,
        rolesToPermissions
      )
      getAssociatedRolesFromInToolPermissions(
        associatedRoles,
        new Set(tool?.inToolPermissions?.map(p => p.slug) || []),
        rolesToPermissions
      )
    }
    return associatedRoles
  }, [toolRequiredPermissionSet, rolesToPermissions, tool?.inToolPermissions])

  const hardEnabledPermissionSlugs = roleAssignedPermissionSlugs || new Set()
  const toolHardEnabled = hardEnabledPermissionSlugs.isSupersetOf(toolRequiredPermissionSet)

  return {
    rolesToPermissions,
    toolRequiredPermissionSet,
    associatedRoles,
    hardEnabledPermissionSlugs,
    toolHardEnabled,
  }
}
